import React, { useState, useEffect, useRef } from 'react';
import { Slide, SlideImage, MarketplaceEvalResponse, hasAnalyzableImage } from '../types';
import { useShop } from '../contexts/ShopContext';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import AuthenticatedImage from './AuthenticatedImage';
import FavoriteButton from './FavoriteButton';
import ImageViewModal from './ImageViewModal';
import MPESearchModal from './MPESearchModal';
import MPEResults from './MPEResults';
import { slideApiService } from '../services/slideApiService';
import { shopLocationService } from '../services/shopLocationService';
import SlideCollectionsPickerModal from './SlideCollectionsPickerModal';

interface SlideViewModalProps {
  slide: Slide;
  onClose: () => void;
  onEdit: (slide: Slide) => void;
}

const SlideViewModal: React.FC<SlideViewModalProps> = ({
  slide,
  onClose,
  onEdit
}) => {
  const { canViewPriceData, canManageSlides, isInternalMember } = useShop();
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [selectedImageDescription, setSelectedImageDescription] = useState<string | undefined>(undefined);
  const [locationName, setLocationName] = useState<string | undefined>(undefined);
  const [placeName, setPlaceName] = useState<string | undefined>(undefined);
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);

  // Image carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageHovered, setImageHovered] = useState(false);
  const [prefetchTriggered, setPrefetchTriggered] = useState(false);
  const isTouchDevice = window.matchMedia('(hover: none)').matches;

  // Swipe/drag refs
  const swipedRef = useRef(false);
  const touchStartRef = useRef<number | null>(null);
  const touchEndRef = useRef<number | null>(null);
  const dragStartRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const minSwipeDistance = 50;

  // Marketplace Eval state
  const [showMPEModal, setShowMPEModal] = useState(false);
  const [mpeResults, setMpeResults] = useState<MarketplaceEvalResponse | null>(null);
  const [showMPEResults, setShowMPEResults] = useState(false);

  // Sort images by display order
  const sortedImages = slide.images ? [...slide.images].sort((a, b) => a.displayOrder - b.displayOrder) : [];

  const hasMultipleImages = sortedImages.length > 1;
  const showArrows = imageHovered && !isTouchDevice;

  // Trigger image prefetch when the modal mounts — immutable image IDs enable aggressive caching
  useEffect(() => {
    triggerPrefetch();
  }, []);

  const triggerPrefetch = () => {
    if (!prefetchTriggered && hasMultipleImages) setPrefetchTriggered(true);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerPrefetch();
    setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerPrefetch();
    setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndRef.current = null;
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (touchStartRef.current === null || touchEndRef.current === null || !hasMultipleImages) {
      touchStartRef.current = null;
      touchEndRef.current = null;
      return;
    }
    const distance = touchStartRef.current - touchEndRef.current;
    if (distance > minSwipeDistance) {
      triggerPrefetch();
      setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
      swipedRef.current = true;
    } else if (distance < -minSwipeDistance) {
      triggerPrefetch();
      setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
      swipedRef.current = true;
    }
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  const onDragStart = (e: React.MouseEvent) => {
    dragStartRef.current = e.clientX;
    isDraggingRef.current = false;
  };

  const onDragMove = (e: React.MouseEvent) => {
    if (dragStartRef.current !== null && Math.abs(e.clientX - dragStartRef.current) > 5) {
      isDraggingRef.current = true;
    }
  };

  const onDragEnd = (e: React.MouseEvent) => {
    if (dragStartRef.current !== null && isDraggingRef.current && hasMultipleImages) {
      const distance = dragStartRef.current - e.clientX;
      if (distance > minSwipeDistance) {
        triggerPrefetch();
        setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
        swipedRef.current = true;
      } else if (distance < -minSwipeDistance) {
        triggerPrefetch();
        setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
        swipedRef.current = true;
      }
    }
    dragStartRef.current = null;
    isDraggingRef.current = false;
  };

  // Fetch location/place names if IDs are present
  useEffect(() => {
    const fetchLocationPlaceNames = async () => {
      if (!slide.locationId && !slide.placeId) {
        setLocationName(undefined);
        setPlaceName(undefined);
        return;
      }

      try {
        // Fetch all locations for the shop
        const locations = await shopLocationService.getShopLocations(slide.shopId, 0, 100, 'name', 'ASC');

        if (slide.placeId) {
          // Find the place across all locations
          for (const location of locations) {
            const place = location.places?.find(p => p.id === slide.placeId);
            if (place) {
              setLocationName(location.name);
              setPlaceName(place.name);
              return;
            }
          }
        } else if (slide.locationId) {
          // Just find the location
          const location = locations.find(l => l.id === slide.locationId);
          if (location) {
            setLocationName(location.name);
            setPlaceName(undefined);
            return;
          }
        }

        // If we get here, location/place wasn't found
        setLocationName(undefined);
        setPlaceName(undefined);
      } catch (error) {
        console.error('Failed to fetch location/place names:', error);
        setLocationName(undefined);
        setPlaceName(undefined);
      }
    };

    fetchLocationPlaceNames();
  }, [slide.shopId, slide.locationId, slide.placeId]);

  // Sort price history by effectiveDate (most recent first)
  const sortedPriceHistory = slide.priceHistory
    ? [...slide.priceHistory].sort((a, b) =>
        new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
      )
    : [];

  const formatCurrency = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (error) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'ACQUISITION':
        return { bg: 'var(--bg-accent)', text: 'var(--primary-light)', border: 'var(--primary-light)' };
      case 'LISTING':
        return { bg: 'var(--success-bg)', text: 'var(--success-dark)', border: 'var(--success)' };
      case 'SOLD':
        return { bg: 'var(--success)', text: 'white', border: 'var(--success)' };
      default:
        return { bg: 'var(--bg-primary)', text: 'var(--text-accent)', border: 'var(--border-light)' };
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'ACQUISITION': return 'Acquisition';
      case 'LISTING': return 'Listing';
      case 'SOLD': return 'Sold';
      default: return type;
    }
  };

  const handleImageClick = (image: SlideImage) => {
    setSelectedImageId(image.id);
    setSelectedImageDescription(image.description);
  };

  const handleDownloadImage = async (e: React.MouseEvent, image: SlideImage) => {
    // Prevent click from bubbling up to parent div (which would open the image viewer)
    e.stopPropagation();

    try {
      // Generate a meaningful filename with display order number (zero-padded)
      const displayOrderStr = String(image.displayOrder + 1).padStart(2, '0');
      const baseFileName = slide.name?.replace(/[^a-z0-9]/gi, '_') || 'image';
      const imageDescription = image.description?.replace(/[^a-z0-9]/gi, '_') || 'image';
      const fileName = `${baseFileName}_${imageDescription}-${displayOrderStr}.jpg`;

      await slideApiService.downloadImage(slide.shopId, slide.id, image.id, fileName);
    } catch (error) {
      console.error('Failed to download image:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  const handleImageNavigation = (imageId: string) => {
    const image = sortedImages.find(img => img.id === imageId);
    if (image) {
      setSelectedImageId(imageId);
      setSelectedImageDescription(image.description);
    }
  };

  const handleEditClick = () => {
    onClose();
    onEdit(slide);
  };

  const handleRunMarketplaceEval = () => {
    setShowMPEModal(true);
  };

  const handleMPEResults = (response: MarketplaceEvalResponse) => {
    setMpeResults(response);
    setShowMPEModal(false);
    setShowMPEResults(true);
  };

  const handleCloseMPEResults = () => {
    setShowMPEResults(false);
    setMpeResults(null);
  };

  // Check if MPE button should be shown (requires manage-slides permission; usage controlled by rate limits)
  const showMPEButton = sortedImages.length > 0 && canManageSlides;
  // An import stub HAS an image but no photo bytes in GCS, so the eval fails server-side.
  // Disable rather than hide — the feature applies, it just needs a real photo first.
  const canRunMPE = hasAnalyzableImage(sortedImages);

  return (
    <>
      {/* Slide View Modal */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: 'var(--bg-card)',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '24px',
              borderBottom: '1px solid var(--border-light)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: '8px'
                }}
              >
                {slide.name}
              </h2>
              {/* External (CLIENT/VENDOR) pricing hint: most-recent LISTING price only (set by the
                  hydration projection; never present for internal slides, which use their own price UI). */}
              {slide.listingPrice != null && (
                <div
                  style={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: 'var(--text-accent)',
                    marginBottom: '8px'
                  }}
                >
                  {slide.listingCurrency && slide.listingCurrency !== 'USD' ? `${slide.listingCurrency} ` : '$'}
                  {slide.listingPrice.toFixed(2)}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {slide.active === false && (
                  <span
                    style={{
                      padding: '4px 12px',
                      backgroundColor: 'var(--error-bg)',
                      color: 'var(--error-text)',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 500
                    }}
                  >
                    INACTIVE
                  </span>
                )}
                {slide.sold && (
                  <span
                    style={{
                      padding: '4px 12px',
                      backgroundColor: 'var(--success)',
                      color: 'var(--text-inverse)',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 500
                    }}
                  >
                    SOLD
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                padding: '0 8px',
                lineHeight: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--text-accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--text-accent)';
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Scrollable Content */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px'
            }}
          >
            {/* Image Carousel */}
            {sortedImages.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div
                  style={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '8px',
                    backgroundColor: 'var(--bg-secondary)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    WebkitUserSelect: 'none'
                  }}
                  onMouseEnter={() => {
                    setImageHovered(true);
                  }}
                  onMouseLeave={() => {
                    setImageHovered(false);
                    dragStartRef.current = null;
                    isDraggingRef.current = false;
                  }}
                  onTouchStart={hasMultipleImages ? onTouchStart : undefined}
                  onTouchMove={hasMultipleImages ? onTouchMove : undefined}
                  onTouchEnd={hasMultipleImages ? onTouchEnd : undefined}
                  onMouseDown={hasMultipleImages ? onDragStart : undefined}
                  onMouseMove={hasMultipleImages ? onDragMove : undefined}
                  onMouseUp={hasMultipleImages ? onDragEnd : undefined}
                  onClick={() => {
                    if (swipedRef.current) {
                      swipedRef.current = false;
                      return;
                    }
                    const currentImg = sortedImages[currentImageIndex];
                    if (currentImg) {
                      handleImageClick(currentImg);
                    }
                  }}
                >
                  {/* Images with prefetch gating */}
                  {sortedImages.map((img, index) => {
                    if (index !== currentImageIndex && !prefetchTriggered) return null;
                    return (
                      <AuthenticatedImage
                        key={img.id}
                        shopId={slide.shopId}
                        slideId={slide.id}
                        imageId={img.id}
                        alt={img.description || `Image ${index + 1}`}
                        loading="eager"
                        size="thumb_800"
                        style={{
                          width: '100%',
                          aspectRatio: '4/3',
                          objectFit: 'cover',
                          pointerEvents: 'none',
                          ...(index !== currentImageIndex ? { position: 'absolute', visibility: 'hidden' } : {})
                        }}
                      />
                    );
                  })}

                  {/* Navigation Arrows */}
                  {hasMultipleImages && (
                    <>
                      <button
                        onClick={handlePrevImage}
                        style={{
                          position: 'absolute',
                          left: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(255, 255, 255, 0.8)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '20px',
                          color: 'var(--text-primary)',
                          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                          opacity: showArrows ? 0.9 : 0,
                          transition: 'opacity 0.2s ease',
                          zIndex: 2,
                          padding: 0,
                          lineHeight: 1,
                          WebkitTapHighlightColor: 'transparent'
                        }}
                        aria-label="Previous image"
                      >
                        ‹
                      </button>
                      <button
                        onClick={handleNextImage}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(255, 255, 255, 0.8)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '20px',
                          color: 'var(--text-primary)',
                          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                          opacity: showArrows ? 0.9 : 0,
                          transition: 'opacity 0.2s ease',
                          zIndex: 2,
                          padding: 0,
                          lineHeight: 1,
                          WebkitTapHighlightColor: 'transparent'
                        }}
                        aria-label="Next image"
                      >
                        ›
                      </button>
                    </>
                  )}

                  {/* Download Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadImage(e, sortedImages[currentImageIndex]);
                    }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease',
                      zIndex: 3
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(27, 58, 107, 0.9)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title="Download full-size image"
                    aria-label="Download image"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </button>

                  {/* Dot Indicators */}
                  {hasMultipleImages && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        zIndex: 2
                      }}
                    >
                      {sortedImages.slice(0, 7).map((_, index) => (
                        <div
                          key={index}
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: index === currentImageIndex ? 'var(--bg-card)'
                              : (sortedImages.length > 7 && currentImageIndex >= 7 && index === 6) ? 'white'
                              : 'rgba(255, 255, 255, 0.5)',
                            transition: 'background-color 0.2s ease'
                          }}
                        />
                      ))}
                    </div>
                  )}

                  <FavoriteButton slideId={slide.id} />
                </div>
              </div>
            )}

            {/* Description */}
            {slide.description && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Description
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {slide.description}
                </p>
              </div>
            )}

            {/* Internal-only content (v1.1.5): marketing copy + Etsy listing helpers + internal notes.
                Gated on isInternalMember so it is NEVER rendered for CLIENT/VENDOR viewers — whose
                hydrated payload also omits these fields entirely. For a CLIENT the "Description" above
                already shows the customer-facing marketing copy (mapped in hydratedToSlide). */}
            {isInternalMember && slide.marketingDescription && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Marketing Description
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {slide.marketingDescription}
                </p>
              </div>
            )}

            {isInternalMember && slide.marketplaceTitle && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Marketplace Title
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {slide.marketplaceTitle}
                </p>
              </div>
            )}

            {isInternalMember && slide.marketplaceTags && slide.marketplaceTags.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Marketplace Tags
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {slide.marketplaceTags.map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        backgroundColor: 'var(--bg-accent)',
                        color: 'var(--primary-dark)',
                        borderRadius: '16px',
                        fontSize: '13px'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isInternalMember && slide.notes && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Notes
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {slide.notes}
                </p>
              </div>
            )}

            {/* Categories */}
            {slide.categories && slide.categories.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Categories
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {slide.categories.map((category, index) => (
                    <span
                      key={index}
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        backgroundColor: 'var(--bg-accent)',
                        color: 'var(--primary-dark)',
                        borderRadius: '16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        border: '1px solid var(--border-medium)'
                      }}
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Location / Place */}
            {(slide.locationId || slide.placeId) && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Location / Place
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {placeName ? (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        backgroundColor: 'var(--bg-accent)',
                        color: 'var(--text-accent)',
                        borderRadius: '16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        border: '1px solid var(--border-light)'
                      }}
                    >
                      {locationName} :: {placeName}
                    </span>
                  ) : locationName ? (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        backgroundColor: 'var(--bg-accent)',
                        color: 'var(--text-accent)',
                        borderRadius: '16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        border: '1px solid var(--border-light)'
                      }}
                    >
                      {locationName}
                    </span>
                  ) : null}
                </div>
              </div>
            )}

            {/* Price History - Only show if user can view price data */}
            {canViewPriceData && sortedPriceHistory.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Price History ({sortedPriceHistory.length})
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {sortedPriceHistory.map((record) => {
                    const typeColors = getTypeColor(record.type);
                    return (
                      <div
                        key={record.id}
                        style={{
                          padding: '12px',
                          border: '1px solid var(--border-light)',
                          borderRadius: '4px',
                          backgroundColor: 'var(--bg-card)'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span
                            style={{
                              padding: '2px 8px',
                              backgroundColor: typeColors.bg,
                              color: typeColors.text,
                              border: `1px solid ${typeColors.border}`,
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 500
                            }}
                          >
                            {getTypeLabel(record.type)}
                          </span>
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: '16px',
                              color: 'var(--text-primary)'
                            }}
                          >
                            {formatCurrency(record.amount, record.currency)}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: 'var(--text-secondary)',
                            marginBottom: record.notes ? '4px' : 0
                          }}
                        >
                          {formatDate(record.effectiveDate)}
                        </div>
                        {record.notes && (
                          <div
                            style={{
                              fontSize: '14px',
                              color: 'var(--text-primary)'
                            }}
                          >
                            {record.notes}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div style={{ marginBottom: '24px' }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Metadata
              </h3>
              <div style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: '1.6' }}>
                {/* created/updated are internal-only — the external (CLIENT/VENDOR) projection omits
                    them, so they'd render as "Unknown date" for CLIENTs. */}
                {isInternalMember && (
                  <>
                    <div>
                      <strong>Created:</strong> {formatDateTime(slide.createdAt)}
                    </div>
                    <div>
                      <strong>Last Updated:</strong> {formatDateTime(slide.updatedAt)}
                    </div>
                  </>
                )}
                <div>
                  <strong>Slide ID:</strong> <code style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{slide.id}</code>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border-light)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}
          >
            {/* Marketplace Eval Button - Full Width */}
            {showMPEButton && (
              <button
                onClick={handleRunMarketplaceEval}
                disabled={!canRunMPE}
                title={canRunMPE ? undefined : 'Add a photo to run Marketplace Eval'}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  border: `1px solid ${canRunMPE ? 'var(--primary-light)' : 'var(--border-light)'}`,
                  backgroundColor: 'transparent',
                  color: canRunMPE ? 'var(--primary-light)' : 'var(--disabled)',
                  borderRadius: '4px',
                  cursor: canRunMPE ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  if (canRunMPE) e.currentTarget.style.backgroundColor = 'var(--bg-accent)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {canRunMPE ? 'Run Marketplace Eval' : 'Add a photo to run Marketplace Eval'}
              </button>
            )}

            {/* Quick add to named collections: any shop-internal role (VIEWER included). */}
            {isInternalMember && (
              <button
                onClick={() => setShowCollectionPicker(true)}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  border: '1px solid var(--primary-light)',
                  backgroundColor: 'transparent',
                  color: 'var(--primary-light)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                Add to Collection
              </button>
            )}

            {/* Standard Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border-medium)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Close
              </button>
              {canManageSlides && (
                <button
                  onClick={handleEditClick}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    backgroundColor: 'var(--primary)',
                    border: 'none',
                    color: 'var(--text-inverse)',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary)';
                  }}
                >
                  Edit Slide
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showCollectionPicker && (
        <SlideCollectionsPickerModal slide={slide} onClose={() => setShowCollectionPicker(false)} />
      )}

      {/* Image View Modal */}
      {selectedImageId && (
        <ImageViewModal
          shopId={slide.shopId}
          slideId={slide.id}
          imageId={selectedImageId}
          imageDescription={selectedImageDescription}
          allImages={sortedImages}
          onNavigate={handleImageNavigation}
          onClose={() => {
            setSelectedImageId(null);
            setSelectedImageDescription(undefined);
          }}
        />
      )}

      {/* Marketplace Eval Search Modal */}
      {showMPEModal && (
        <MPESearchModal
          slideId={slide.id}
          shopId={slide.shopId}
          suggestedQuery={slide.name || ''}
          slideImages={sortedImages}
          onClose={() => setShowMPEModal(false)}
          onResults={handleMPEResults}
        />
      )}

      {/* Marketplace Eval Results Modal */}
      {showMPEResults && mpeResults && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '20px'
          }}
          onClick={handleCloseMPEResults}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-card)',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '1200px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <MPEResults
              response={mpeResults}
              onClose={handleCloseMPEResults}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SlideViewModal;
